import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';

// §10.4 — metric definitions are read off the API response
// (metric_definitions), never hand-typed here. This is the shared
// tooltip/definitions UI for every admin reporting surface
// (AdminReportsPage.js, AdminFacultyDrilldownPage.js) — built once here so a
// second page never grows its own copy.
//
// InfoTooltip is a from-scratch popover, not a `title` attribute: a `title`
// on an <svg> renders nothing (SVG needs a <title> CHILD NODE, not the HTML
// attribute) and is invisible on touch and to keyboard users regardless.
// Opens on hover, click (pinned — stays open after the pointer leaves) AND
// keyboard focus; closes on Escape (returns focus to the trigger) and on an
// outside click while pinned.

export function InfoTooltip({ id, text, placement = 'bottom' }) {
  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const open = hovering || focused || pinned;
  const tooltipId = `report-tooltip-${id}`;

  useEffect(() => {
    if (!pinned) return undefined;
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setPinned(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [pinned]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setPinned(false);
        setHovering(false);
        setFocused(false);
        if (triggerRef.current) triggerRef.current.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  if (!text) return null;

  return (
    <span className="relative inline-flex" ref={wrapperRef}>
      <button
        type="button"
        ref={triggerRef}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={() => setPinned((p) => !p)}
        className="inline-flex text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-help rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:focus-visible:ring-accent-cool"
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        aria-label={`What does "${text.length > 40 ? text.slice(0, 40) + '…' : text}" mean? Definition`}
        data-testid={`th-info-${id}`}
      >
        <Info size={12} />
      </button>
      {open && (
        <span
          id={tooltipId}
          role="tooltip"
          data-testid={`th-info-tooltip-${id}`}
          className={`absolute left-1/2 z-30 w-64 -translate-x-1/2 rounded-md bg-slate-800 dark:bg-slate-950 px-3 py-2 text-xs font-normal normal-case leading-snug text-white shadow-lg ${
            placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          {text}
        </span>
      )}
    </span>
  );
}

export function Th({ children, defId, definitions, ...thProps }) {
  return (
    <th className="px-4 py-3 text-left font-medium text-slate-500" {...thProps}>
      <span className="inline-flex items-center gap-1">
        {children}
        <InfoTooltip id={defId} text={definitions ? definitions[defId] : undefined} />
      </span>
    </th>
  );
}

// `columns` — [{id, label}] in display order (the report's REPORT_COLUMNS or
// the drill-down's DRILLDOWN_COLUMNS, both sourced from the backend).
export function HowToReadPanel({ definitions, columns }) {
  const [open, setOpen] = useState(false);
  if (!definitions || !columns) return null;
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="how-to-read-toggle"
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
      >
        <span className="inline-flex items-center gap-2">
          <Info size={14} className="text-slate-400" />
          How to read this page
        </span>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>
      {open && (
        <dl className="px-4 pb-4 divide-y divide-slate-100 dark:divide-slate-700" data-testid="how-to-read-panel">
          {columns.map((col) => (
            <div key={col.id} className="py-2.5">
              <dt className="text-sm font-semibold text-slate-800 dark:text-slate-200">{col.label}</dt>
              <dd className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">{definitions[col.id]}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
